import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token and selected marca context to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('finscope_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // If Super Admin has selected a marca, add cliente_id to params for context
  const selectedClienteId = localStorage.getItem('selectedClienteId');
  if (selectedClienteId) {
    // Add cliente_id as a query param for data scoping (for Super Admin context)
    if (!config.params) {
      config.params = {};
    }
    // Only add if not already specified
    if (!config.params.cliente_id) {
      config.params.cliente_id = selectedClienteId;
    }
  }
  
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('finscope_token');
      localStorage.removeItem('finscope_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const auth = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
};

// Clientes
export const clientes = {
  getAll: () => api.get('/clientes'),
  getOne: (id) => api.get(`/clientes/${id}`),
  create: (data) => api.post('/clientes', data),
  update: (id, data) => api.put(`/clientes/${id}`, data),
};

// Cuentas
export const cuentas = {
  getAll: (clienteId) => api.get('/cuentas', { params: { cliente_id: clienteId } }),
  create: (data) => api.post('/cuentas', data),
  update: (id, data) => api.put(`/cuentas/${id}`, data),
};

// Categorías
export const categorias = {
  getAll: () => api.get('/categorias'),
  create: (data) => api.post('/categorias', data),
  update: (id, data) => api.put(`/categorias/${id}`, data),
};

// Reglas
export const reglas = {
  getAll: () => api.get('/reglas'),
  create: (data) => api.post('/reglas', data),
  update: (id, data) => api.put(`/reglas/${id}`, data),
  delete: (id) => api.delete(`/reglas/${id}`),
};

// Movimientos
export const movimientos = {
  getAll: (params) => api.get('/movimientos', { params }),
  getCount: (params) => api.get('/movimientos/count', { params }),
  create: (data) => api.post('/movimientos', data),
  upload: (file, cuentaId) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/movimientos/upload?cuenta_id=${cuentaId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // New validation flow
  analyze: (file, cuentaId) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/movimientos/analyze?cuenta_id=${cuentaId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  confirm: (uploadId, forzarTodos = false) => api.post(`/movimientos/confirm/${uploadId}?forzar_todos=${forzarTodos}`),
  cancelUpload: (uploadId) => api.delete(`/movimientos/cancel/${uploadId}`),
  getHistorial: (limite = 20) => api.get('/movimientos/historial', { params: { limite } }),
  deleteHistorial: (historialId) => api.delete(`/movimientos/historial/${historialId}`),
  deleteAll: (confirmacion) => api.delete('/movimientos/eliminar-todos', { params: { confirmacion } }),
  verificar: (batchId) => api.get(`/movimientos/verificar/${batchId}`),
  update: (id, data) => api.put(`/movimientos/${id}`, data),
  delete: (id) => api.delete(`/movimientos/${id}`),
  downloadExcel: (params) => api.get('/movimientos/download-excel', { 
    params, 
    responseType: 'blob' 
  }),
  downloadPlantilla: () => api.get('/movimientos/plantilla', { 
    responseType: 'blob' 
  }),
  getRangoFechas: () => api.get('/movimientos/rango-fechas'),
  getClientesUnicos: (params) => api.get('/movimientos/clientes-unicos', { params }),
};

// Pagos
export const pagos = {
  getAll: (params) => api.get('/pagos', { params }),
  getOne: (id) => api.get(`/pagos/${id}`),
  create: (data) => api.post('/pagos', data),
  update: (id, data) => api.put(`/pagos/${id}`, data),
  updatePriority: (id, prioridad) => api.patch(`/pagos/${id}/prioridad`, { prioridad }),
  delete: (id) => api.delete(`/pagos/${id}`),
  upload: (file, cuentaId, periodo) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/pagos/upload?cuenta_id=${cuentaId}&periodo=${periodo}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  archivePeriod: (periodo) => api.post(`/pagos/archive/${periodo}`),
  generarRecurrentes: (periodo) => api.post('/pagos/generar-recurrentes', null, { params: { periodo } }),
};

// Dashboard
export const dashboard = {
  getKpis: (params) => api.get('/dashboard/kpis', { params }),
  getTendencias: (params) => api.get('/dashboard/tendencias', { params }),
  getGastosPorCategoria: (params) => api.get('/dashboard/gastos-categoria', { params }),
  getFlujoCaja: (params) => api.get('/dashboard/flujo-caja', { params }),
  getClientesPagos: (params) => api.get('/dashboard/clientes-pagos', { params }),
};

// Usuarios
export const usuarios = {
  getAll: () => api.get('/usuarios'),
  create: (data) => api.post('/usuarios', data),
};

// Demo
export const demo = {
  setup: () => api.post('/demo/setup'),
};

// Rentabilidad
export const colaboradores = {
  getAll: () => api.get('/colaboradores'),
  create: (data) => api.post('/colaboradores', data),
  update: (id, data) => api.put(`/colaboradores/${id}`, data),
  delete: (id) => api.delete(`/colaboradores/${id}`),
};

export const herramientas = {
  getAll: () => api.get('/herramientas'),
  create: (data) => api.post('/herramientas', data),
  update: (id, data) => api.put(`/herramientas/${id}`, data),
  delete: (id) => api.delete(`/herramientas/${id}`),
};

export const asignacionesColaboradores = {
  getAll: (params) => api.get('/asignaciones-colaboradores', { params }),
  create: (data) => api.post('/asignaciones-colaboradores', data),
  delete: (id) => api.delete(`/asignaciones-colaboradores/${id}`),
};

export const usoHerramientas = {
  getAll: (params) => api.get('/uso-herramientas', { params }),
  create: (data) => api.post('/uso-herramientas', data),
  delete: (id) => api.delete(`/uso-herramientas/${id}`),
};

export const ingresosCuenta = {
  getAll: (params) => api.get('/ingresos-cuenta', { params }),
  create: (data) => api.post('/ingresos-cuenta', data),
  delete: (id) => api.delete(`/ingresos-cuenta/${id}`),
};

export const financiamientos = {
  getAll: (params) => api.get('/financiamientos', { params }),
  create: (data) => api.post('/financiamientos', data),
  update: (id, data) => api.put(`/financiamientos/${id}`, data),
  delete: (id) => api.delete(`/financiamientos/${id}`),
};

export const rentabilidad = {
  getResumen: (params) => api.get('/rentabilidad/resumen', { params }),
  getCuentas: (params) => api.get('/rentabilidad/cuentas', { params }),
  getColaboradores: () => api.get('/rentabilidad/colaboradores'),
};

// Rentabilidad V2 - Client-based structure (HAUS)
export const clientesRentabilidad = {
  getAll: (params) => api.get('/clientes-rentabilidad', { params }),
  getOne: (id) => api.get(`/clientes-rentabilidad/${id}`),
  create: (data) => api.post('/clientes-rentabilidad', data),
  update: (id, data) => api.put(`/clientes-rentabilidad/${id}`, data),
  delete: (id) => api.delete(`/clientes-rentabilidad/${id}`),
};

export const ingresosCliente = {
  getAll: (params) => api.get('/ingresos-cliente', { params }),
  create: (data) => api.post('/ingresos-cliente', data),
  update: (id, data) => api.put(`/ingresos-cliente/${id}`, data),
  delete: (id) => api.delete(`/ingresos-cliente/${id}`),
};

export const asignacionesColaboradoresCliente = {
  getAll: (params) => api.get('/asignaciones-colaboradores-cliente', { params }),
  create: (data) => api.post('/asignaciones-colaboradores-cliente', data),
  delete: (id) => api.delete(`/asignaciones-colaboradores-cliente/${id}`),
};

export const usoHerramientasCliente = {
  getAll: (params) => api.get('/uso-herramientas-cliente', { params }),
  create: (data) => api.post('/uso-herramientas-cliente', data),
  delete: (id) => api.delete(`/uso-herramientas-cliente/${id}`),
};

export const financiamientosCliente = {
  getAll: (params) => api.get('/financiamientos-cliente', { params }),
  create: (data) => api.post('/financiamientos-cliente', data),
  update: (id, data) => api.put(`/financiamientos-cliente/${id}`, data),
  delete: (id) => api.delete(`/financiamientos-cliente/${id}`),
};

export const rentabilidadV2 = {
  getResumen: (params) => api.get('/rentabilidad-v2/resumen', { params }),
  getClientes: (params) => api.get('/rentabilidad-v2/clientes', { params }),
  getCuentas: (params) => api.get('/rentabilidad-v2/cuentas', { params }),
  getClienteDetalle: (id, params) => api.get(`/rentabilidad-v2/cliente/${id}`, { params }),
  getColaboradores: (params) => api.get('/rentabilidad-v2/colaboradores', { params }),
};

// ============= MÓDULOS PREMIUM =============

// Presupuestos
export const presupuestos = {
  getAll: (params) => api.get('/presupuestos', { params }),
  getResumen: (params) => api.get('/presupuestos/resumen', { params }),
  create: (data) => api.post('/presupuestos', data),
  update: (id, data) => api.put(`/presupuestos/${id}`, data),
  delete: (id) => api.delete(`/presupuestos/${id}`),
};

// Forecast
export const forecast = {
  get: (params) => api.get('/forecast', { params }),
};

// Proyectos
export const proyectos = {
  getAll: (params) => api.get('/proyectos', { params }),
  getDetalle: (id) => api.get(`/proyectos/${id}/detalle`),
  create: (data) => api.post('/proyectos', data),
  update: (id, data) => api.put(`/proyectos/${id}`, data),
  delete: (id) => api.delete(`/proyectos/${id}`),
  addCosto: (id, data) => api.post(`/proyectos/${id}/costos`, data),
  addIngreso: (id, data) => api.post(`/proyectos/${id}/ingresos`, data),
};

// Equipo (Colaboradores para módulo premium)
export const equipo = {
  getAll: (params) => api.get('/colaboradores', { params }),
  getAsignaciones: (colaboradorId) => api.get(`/colaboradores/${colaboradorId}/asignaciones`),
  create: (data) => api.post('/colaboradores', data),
  update: (id, data) => api.put(`/colaboradores/${id}`, data),
  delete: (id) => api.delete(`/colaboradores/${id}`),
  createAsignacion: (data) => api.post('/asignaciones-colaboradores', data),
  deleteAsignacion: (id) => api.delete(`/asignaciones-colaboradores/${id}`),
};

// Reportes Automáticos
export const reportesAuto = {
  getAll: (params) => api.get('/reportes-programados', { params }),
  create: (data) => api.post('/reportes-programados', data),
  update: (id, data) => api.put(`/reportes-programados/${id}`, data),
  delete: (id) => api.delete(`/reportes-programados/${id}`),
  enviar: (id) => api.post(`/reportes-programados/${id}/enviar`),
};

// AI Advisor
export const aiAdvisor = {
  chat: (pregunta) => api.post('/ai-advisor/chat', { pregunta }),
};

// Facturación
export const facturacion = {
  getDashboard: (params) => api.get('/facturacion/dashboard', { params }),
  getMonthlyChart: (params) => api.get('/facturacion/charts/monthly', { params }),
  getByDimension: (dimension, params) => api.get('/facturacion/charts/by-dimension', { params: { ...params, dimension } }),
  getInvoices: (params) => api.get('/facturacion/invoices', { params }),
  getInvoiceDetail: (id) => api.get(`/facturacion/invoices/${id}`),
  getFilterOptions: () => api.get('/facturacion/filter-options'),
  getSyncStatus: () => api.get('/facturacion/sync-status'),
  deleteDemoData: () => api.delete('/facturacion/demo-data'),
};

export default api;
